import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

import { portalApi } from '../api';

import meKeys from './models/me/keys';
import pinsAdminUserKeys from './models/pinsAdminUsers/keys';
import { camelCaseApiResponse, deleteInfiniteListsItem, getPageData, modifyInfiniteListsItem } from './util';

/* QUERIES
========================================================= */

export const useMeData = (options = {}) => useQuery(
  meKeys.me,
  () => camelCaseApiResponse(() => portalApi.getUser()),
  {
    retry: false,
    ...options
  }
);

export const useMeCompanies = (options = {}) => useQuery(
  meKeys.companies,
  () => camelCaseApiResponse(() => portalApi.listCompanies(false)),
  {
    retry: false,
    ...options
  }
);

/**
 * PINS admin: search all users across all companies by name/email (server-side).
 * Only enabled once the search term is at least 2 characters long.
 */
export const useSearchUsers = ({ name = '', email = '', pageNumber = 1, pageSize = 25 } = {}, options = {}) => {
  const term = name || email || '';

  return useQuery(
    pinsAdminUserKeys.search(term),
    () => camelCaseApiResponse(() => portalApi.searchUsers({ name, email, pageNumber, pageSize })),
    {
      enabled: term.trim().length >= 2,
      keepPreviousData: true,
      ...options
    }
  );
};

export const useForgotPassword = (options = {}) => useMutation(
  ({ email }) => camelCaseApiResponse(() => portalApi.forgotPassword(email)),
  { ...options }
);

export const useResetPassword = (options = {}) => useMutation(
  ({ userId, password, key }) => camelCaseApiResponse(() => portalApi.createUserPassword(userId, password, key)),
  { ...options }
);

/* MUTATIONS
========================================================= */

/**
 * PINS admin: update a user's contact info (email, first/last name, phone).
 */
export const useUpdateUser = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ userId, ...userData }) => camelCaseApiResponse(() => portalApi.updateUser(userId, userData)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(pinsAdminUserKeys.all);
      },
      ...options
    }
  );
};
